/**
 * Staff-related models
 */

/**
 * Staff role enum
 */
export enum StaffRole {
  ADMIN = 0,
  DOCK_MASTER = 1,
  OFFICE_ADMIN = 2,
  SERVICE_MANAGER = 3,
  SERVICE_TECH = 4,
  DECK_HAND = 5
}

/**
 * Staff break time
 */
export interface StaffBreak {
  id: string;
  start: string; // HH:mm format
  end: string; // HH:mm format
}

/**
 * Staff schedule for a single day
 */
export interface DayStaffSchedule {
  start: string; // HH:mm format
  end: string; // HH:mm format
  isOff: boolean;
  breaks: StaffBreak[];
}

/**
 * Staff schedule for all days of the week
 */
export interface StaffSchedule {
  [day: string]: DayStaffSchedule; // e.g., "Monday", "Tuesday", etc.
}

/**
 * Staff DTO - Full staff member information
 */
export interface StaffDto {
  id: string;
  firstName: string;
  lastName: string;
  /** Display name: firstName + lastName (for backward compatibility) */
  name: string;
  email: string;
  phone?: string | null;
  /** Numeric enum or string name when API uses JsonStringEnumConverter. */
  staffRole?: StaffRole | string | null;
  jobTitle?: string | null;
  reportsToUserId?: string | null;
  reportsToName?: string | null;
  staffSchedule?: StaffSchedule | null;
  accessibleMarinaIds: string[];
  marinaRoles: string[]; // Roles for the current marina context
  isActive: boolean;
  isLockedOut?: boolean;
  lockoutEnd?: string | null;
  accessFailedCount?: number;
  /** Derived account status from staff list: Active, Inactive, or Blocked. */
  accountStatus?: string;
  lastLogin?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/**
 * Create Staff Request
 */
export interface CreateStaffRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string | null;
  password?: string | null; // Optional - if not provided, backend will generate random password and send via email
  staffRole: StaffRole;
  jobTitle?: string | null;
  reportsToUserId?: string | null;
  marinaId: string; // Marina to assign staff to
  roleName: string; // ADMIN, DOCK_MASTER, etc.
  staffSchedule?: StaffSchedule | null;
}

/**
 * Update Staff Request
 */
export interface UpdateStaffRequest {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  staffRole?: StaffRole | null;
  jobTitle?: string | null;
  reportsToUserId?: string | null;
  staffSchedule?: StaffSchedule | null;
  isActive?: boolean;
  roleName?: string | null; // Marina role name (ADMIN, DOCK_MASTER, etc.) for the current marina context
}

/** API may send StaffRole as a number or string (JsonStringEnumConverter). */
export type StaffRoleInput = StaffRole | string | number | null | undefined;

const STAFF_ROLE_BY_NAME: Record<string, StaffRole> = {
  ADMIN: StaffRole.ADMIN,
  DOCK_MASTER: StaffRole.DOCK_MASTER,
  DOCKMASTER: StaffRole.DOCK_MASTER,
  OFFICE_ADMIN: StaffRole.OFFICE_ADMIN,
  SERVICE_MANAGER: StaffRole.SERVICE_MANAGER,
  SERVICE_TECH: StaffRole.SERVICE_TECH,
  DECK_HAND: StaffRole.DECK_HAND,
};

/** Normalize StaffRole from API JSON (number or enum string). */
export function parseStaffRole(value: StaffRoleInput): StaffRole | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Object.values(StaffRole).includes(value) ? (value as StaffRole) : null;
  }
  if (typeof value === 'string') {
    const key = value.trim().toUpperCase().replace(/\s+/g, '_');
    return STAFF_ROLE_BY_NAME[key] ?? null;
  }
  return null;
}

/** Prefer profile staffRole; fall back to marina role name from UserMarinaRole. */
export function resolveStaffRoleFromDto(
  staff: Pick<StaffDto, 'staffRole' | 'marinaRoles'>
): StaffRole | null {
  return parseStaffRole(staff.staffRole) ?? parseStaffRole(staff.marinaRoles?.[0]);
}

export function getStaffRoleDisplayName(value: StaffRoleInput): string {
  switch (parseStaffRole(value)) {
    case StaffRole.ADMIN:
      return 'Admin';
    case StaffRole.DOCK_MASTER:
      return 'Dock Master';
    case StaffRole.OFFICE_ADMIN:
      return 'Office Admin';
    case StaffRole.SERVICE_MANAGER:
      return 'Service Manager';
    case StaffRole.SERVICE_TECH:
      return 'Service Tech';
    case StaffRole.DECK_HAND:
      return 'Deck Hand';
    default:
      return 'Staff';
  }
}

export function getStaffRoleBadgeClass(value: StaffRoleInput): string {
  switch (parseStaffRole(value)) {
    case StaffRole.ADMIN:
      return 'badge-admin';
    case StaffRole.DOCK_MASTER:
      return 'badge-dock-master';
    case StaffRole.OFFICE_ADMIN:
      return 'badge-office-admin';
    case StaffRole.SERVICE_MANAGER:
      return 'badge-service-manager';
    case StaffRole.SERVICE_TECH:
      return 'badge-service-tech';
    case StaffRole.DECK_HAND:
      return 'badge-deck-hand';
    default:
      return 'badge-default';
  }
}

